/**
 * UsersListFilter Component
 *
 * This component provides a filter UI for the users list, allowing filtering by project, dispatch mode, status, and dispatch stage.
 * It synchronizes filter state with URL parameters, fetches filter options from APIs, and updates the query state for data requests.
 *
 * Features:
 * - Fetches filter options and dispatch status from backend APIs
 * - Syncs filter selections with URL search parameters
 * - Updates global query state for data filtering
 * - Provides reset and apply actions for filters
 * - Dynamically updates dependent filter fields (e.g., status and subStatus based on dispatch mode)
 */

import { useEffect, useState } from 'react'
import { MenuComponent } from '../../_metronic/assets/ts/components'
import { initialQueryState, KTSVG } from '../../_metronic/helpers'
import { useQueryRequest } from './request'
import { useQueryResponse } from './response'
import { getDispatchStatus, getFilterOption } from '../master/fullfillment/api'
import { useSearchParams } from 'react-router-dom';
const UsersListFilter = () => {
    const { updateState } = useQueryRequest();
    const { isLoading } = useQueryResponse();
    const [filterOption, setFilterOption] = useState()
    const [filterState, setFilterState] = useState([
        { name: '', type: 'project', value: '' },
        { name: '', type: 'dispatch_mode', value: '' },
        { name: '', type: 'status', value: '' },
        { name: '', type: 'subStatus', value: '' },
    ]);

    const [subStatusOptions, setsubStatusOptions] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams(); // For managing URL parameters
    const getFilter = () => {
        getFilterOption('dispatch').then((d) => setFilterOption(d.data));
    };
    useEffect(() => {
        getDispatchStatus().then((d) => setsubStatusOptions(d?.data));
    }, []);
    useEffect(() => {
        MenuComponent.reinitialization();
        getFilter();

        // Get filter values from URL
        const proParam = searchParams.get('project');
        const statusParam = searchParams.get('status');
        const dispatch_modeParam = searchParams.get('dispatch_mode');
        const subStatusParam = searchParams.get('subStatus');
        const projectName = searchParams.get('projectName');
        const dispatchModeName = searchParams.get('dispatchModeName');
        const statusName = searchParams.get('statusName');
        const subStatusName = searchParams.get('subStatusName');

        // Update state with URL parameters


        if (proParam || statusParam || dispatch_modeParam || subStatusParam) {
            const updateStat = [
                { name: projectName, type: 'project', value: proParam || '' },
                { name: dispatchModeName, type: 'dispatch_mode', value: dispatch_modeParam || '' },
                { name: statusName, type: 'status', value: statusParam || '' },
                { name: subStatusName, type: 'subStatus', value: subStatusParam || '' },
            ];
            setFilterState(updateStat);
            updateFilterState(updateStat);
        }
    }, [searchParams]);

    const resetData = () => {
        setSearchParams({});
        setFilterState([
            { name: '', type: 'project', value: '' },
            { name: '', type: 'dispatch_mode', value: '' },
            { name: '', type: 'status', value: '' },
            { name: '', type: 'subStatus', value: '' },
        ]);
        updateState({ filter: undefined, ...initialQueryState });
    };

    const filterData = () => {
        setSearchParams({
            project: filterState[0]?.value,
            status: filterState[2]?.value,
            dispatch_mode: filterState[1]?.value,
            subStatus: filterState[3]?.value,
            projectName: filterState[0]?.name,
            dispatchModeName: filterState[1]?.name,
            statusName: filterState[2]?.name,
            subStatusName: filterState[3]?.name,
        });
        updateFilterState(filterState);
    };

    const updateFilterState = (filterState) => {
        updateState({
            filter: {
                project: filterState[0]?.value,
                status: filterState[2]?.value,
                dispatch_mode: filterState[1]?.value,
                subStatus: filterState[3]?.value,
            },
            ...initialQueryState,
        });
    }
    const updateFilterName = (type, name, value) => {
        setFilterState(prevState => {
            const newState = prevState.map(filter => {
                // If filter type matches, update its name and value
                if (filter.type === type) {
                    // If value is empty, reset name to an empty string
                    return { ...filter, name: value ? name : '', value };
                }
                return filter;
            });

            // Reset fields based on the changes
            if (type === 'dispatch_mode') {
                // If 'dispatch_mode' (index 1) is changed, reset index 2 and 3
                newState[2] = { ...newState[2], name: '', value: '' };
                newState[3] = { ...newState[3], name: '', value: '' };
            }

            if (type === 'status') {
                // If 'status' (index 2) is changed, reset index 3
                newState[3] = { ...newState[3], name: '', value: '' };
            }

            return newState;
        });
    };

    const handleFilterChange = (type, value, name) => {
        const newName = (name.split('- ')[0]).trim();
        updateFilterName(type, newName, value);  // Update state when the filter changes
    };

    const filteredFilters = filterState.filter(filter => filter.name && filter.name.trim() !== '').length;

    return (
        <>
            <div>
                <button
                    disabled={isLoading}
                    type='button'
                    className='btn btn-light-primary  position-relative me-3'
                    data-kt-menu-trigger='click'
                    data-kt-menu-placement='bottom-end'
                >
                    {filteredFilters > 0 &&
                        <span className="position-absolute top-0 start-100 translate-middle  badge badge-circle badge-success">{filteredFilters}</span>
                    }
                    <KTSVG path='/media/icons/duotune/general/gen031.svg' className='svg-icon-2' />
                    Filter
                </button>
                <div className='menu menu-sub menu-sub-dropdown w-300px w-md-325px' data-kt-menu='true'>
                    <div className='px-7 py-5'>
                        <div className='fs-5 text-dark fw-semibolder'>Filter Options</div>
                    </div>
                    <div className='separator border-gray-200'></div>
                    <div className='px-7 py-5' data-kt-user-table-filter='form'>
                        <div>
                            <label className='form-label fs-5 fw-semibold'>Select Project:</label>
                            <select
                                className='form-select form-select-sm form-select-solid fs-6'
                                onChange={(e) => handleFilterChange('project', e.target.value, e.target.selectedOptions[0].text)}
                                value={filterState[0].value}
                            >
                                <option value=''>Select</option>
                                {filterOption?.project?.length > 0 &&
                                    filterOption.project.map((pro) => (
                                        <option key={pro?.id} value={pro?.id}>
                                            {pro?.project_name} - ({pro?.order_count})
                                        </option>
                                    ))}
                            </select>
                        </div>
                        <div className="row">
                            <div className="col-lg-12">
                                <label className="form-label fw-semibold">Dispatch Mode:</label>
                                <select
                                    value={filterState[1].value}
                                    onChange={(e) => handleFilterChange('dispatch_mode', e.target.value, e.target.selectedOptions[0].text)}
                                    className="form-select form-select-sm form-select-solid fs-6"
                                >
                                    <option value="">Select Dispatch Mode</option>
                                    {subStatusOptions?.mode?.map((item) => (
                                        <option key={item.id} value={item.id}>
                                            {item.name} - ({item?.order_count})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-lg-12">
                                <label className="form-label fw-semibold">Status:</label>
                                <select
                                    value={filterState[2].value}
                                    onChange={(e) => handleFilterChange('status', e.target.value, e.target.selectedOptions[0].text)}
                                    className="form-select form-select-sm form-select-solid fs-6"
                                >
                                    <option value="">Select Status</option>
                                    {filterState[1].value &&
                                        Object.keys(subStatusOptions?.status?.[filterState[1].value] || {}).map((status) => (
                                            <option key={status} value={status}>
                                                {status}
                                            </option>
                                        ))
                                    }
                                </select>
                            </div>
                            <div className="col-lg-12">
                                <label className="form-label fw-semibold">Dispatch Stage:</label>
                                <select
                                    value={filterState[3].value}
                                    onChange={(e) => handleFilterChange('subStatus', e.target.value, e.target.selectedOptions[0].text)}
                                    className="form-select form-select-sm form-select-solid fs-6"
                                >
                                    <option value="">Select Stage</option>
                                    {filterState[1].value &&
                                        filterState[2].value &&
                                        (subStatusOptions?.status?.[filterState[1].value]?.[filterState[2].value] || []).map((subStatus, index) => (
                                            <option key={index} value={subStatus?.id}>
                                                {subStatus?.status}
                                            </option>
                                        ))
                                    }
                                </select>
                            </div>
                        </div>
                        <div className='d-flex justify-content-end pt-4'>
                            <button
                                type='button'
                                disabled={isLoading}
                                onClick={resetData}
                                className='btn btn-light btn-active-light-primary fw-semibold me-2 px-6'
                                data-kt-menu-dismiss='true'
                            >
                                Reset
                            </button>
                            <button
                                disabled={isLoading}
                                type='button'
                                onClick={filterData}
                                className='btn btn-primary fw-semibold px-6'
                                data-kt-menu-dismiss='true'
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}



export { UsersListFilter }